import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TileLayer, Marker, Polyline, CircleMarker, Tooltip, useMap } from "react-leaflet";
import { tileUrlFor, MAP_ATTRIBUTION, MAP_SUBDOMAINS } from "../lib/basemap";
import { useTheme } from "next-themes";
import L from "leaflet";
import { Radio, X, Crosshair, CheckCircle2, Search, Globe } from "lucide-react";
import { live } from "@/lib/api";
import { boardPhase, BOARD_PHASES, phaseTone } from "@/lib/format";
import SafeMap from "@/components/StrictModeSafeMap";
import { AIRPORTS, airportLatLon, haversineNm, greatCircleArc, splitRouteAt } from "@/lib/airports";

/**
 * VA Live Map — cockpit-view tracker inspired by MyVac.Live.
 * Sidebar: search + filters + stats + scrollable flight list.
 * Map: dark tiles, rotated plane silhouettes, dashed great-circle arcs
 *      (red for flown, grey for ahead), airport labels.
 * Detail overlay: pilot info, progress bar, telemetry grid, follow toggle.
 */

const RED   = "#e31937";       // AC red
const GREY  = "#8a9098";
const CAT_SIZE = { wide: 30, narrow: 24, regional: 20, turbo: 20 };
const AC_CAT = {
  B77W: "wide", B77L: "wide", B772: "wide", B773: "wide",
  A333: "wide", A332: "wide", A338: "wide", A339: "wide",
  A359: "wide", A35K: "wide", B789: "wide", B788: "wide", B78X: "wide",
  A321: "narrow", A320: "narrow", A319: "narrow", A20N: "narrow", A21N: "narrow",
  B7M8: "narrow", B38M: "narrow", B39M: "narrow", B737: "narrow", B738: "narrow", B739: "narrow",
  BCS1: "narrow", BCS3: "narrow", A220: "narrow",
  CRJ9: "regional", CRJ7: "regional", CRJ2: "regional",
  E175: "regional", E170: "regional", E190: "regional", E195: "regional",
  DH8A: "turbo", DH8B: "turbo", DH8C: "turbo", DH8D: "turbo",
  B190: "turbo", AT72: "turbo", AT75: "turbo", AT76: "turbo",
};

const JET_SVG = `<path d="M12 2 L13 9 L22 12 L22 14 L13 13 L13 19 L15 20 L15 21 L9 21 L9 20 L11 19 L11 13 L2 14 L2 12 L11 9 Z" fill="currentColor"/>`;
const TURBO_SVG = `<path d="M12 3 L13 9 L22 10 L22 12 L13 12 L13 15 L18 16 L18 17 L13 17 L13 20 L15 21 L15 22 L9 22 L9 21 L11 20 L11 17 L6 17 L6 16 L11 15 L11 12 L2 12 L2 10 L11 9 Z" fill="currentColor"/>`;

/** Heading as 0-359, or null when the sim never gave us one.
 *
 * `Number(f.hdg) || 0` was the old reading, and it is the house habit the
 * backlog names "null coerced to zero": a flight with NO heading rendered as a
 * plane confidently pointing due north, indistinguishable from one actually
 * flying 360. Unknown has to look unknown or we cannot tell a bad rotation
 * from absent telemetry. 0 is also falsy, so `|| 0` mangled a genuine 360/0.
 */
function normHdg(v) {
  const n = Number(v);
  if (v === null || v === undefined || v === "" || !Number.isFinite(n)) return null;
  return ((n % 360) + 360) % 360;
}

function planeIcon(hdg = null, cat = "narrow", selected = false) {
  const size = (CAT_SIZE[cat] || 24) + (selected ? 8 : 0);
  const color = selected ? RED : "hsl(var(--foreground))";

  // No heading: a ring, not an aircraft. Says "here, direction unknown".
  if (hdg === null) {
    const r = Math.round(size * 0.3);
    return L.divIcon({
      className: "mvl-plane-icon",
      html: `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 0 4px var(--map-halo))">
        <div style="width:${r * 2}px;height:${r * 2}px;border-radius:50%;border:2px solid ${color};box-sizing:border-box"></div>
      </div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  const path = cat === "turbo" ? TURBO_SVG : JET_SVG;
  // JET_SVG/TURBO_SVG are drawn nose-up, so 0deg already IS north and the
  // rotation is the raw heading. This used to subtract 45: that offset belongs
  // to the U+2708 glyph these paths replaced, which points north-EAST. The
  // glyph went, the correction stayed, and every aircraft on the map has been
  // drawn 45 degrees counter-clockwise of its actual heading ever since.
  return L.divIcon({
    className: "mvl-plane-icon",
    html: `<div style="width:${size}px;height:${size}px;transform:rotate(${hdg}deg);color:${color};filter:drop-shadow(0 0 4px var(--map-halo))">
      <svg viewBox="0 0 24 24" style="width:100%;height:100%">${path}</svg>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function aptIcon(code) {
  return L.divIcon({
    className: "mvl-apt-label",
    html: `<div style="font:600 10px/1 'JetBrains Mono',monospace;color:#e8eaed;background:rgba(11,15,20,0.85);padding:2px 5px;border-radius:2px;letter-spacing:0.05em">${code}</div>`,
    iconSize: [0, 0],
    iconAnchor: [-6, 6],
  });
}

/** Auto-pan when following a flight. */
function FollowSelected({ selected, follow }) {
  const map = useMap();
  useEffect(() => {
    if (!selected || !follow) return;
    const lat = Number(selected.pos_lat);
    const lon = Number(selected.pos_long);
    if (lat && lon) map.flyTo([lat, lon], Math.max(map.getZoom(), 5), { duration: 0.8 });
  }, [selected, follow, map]);
  return null;
}

function normalize(f) {
  const lat = Number(f.pos_lat) || 0;
  const lon = Number(f.pos_long) || 0;
  const oIcao = String(f.orig || "").toUpperCase();
  const dIcao = String(f.dest || "").toUpperCase();
  const oApt = AIRPORTS[oIcao];
  const dApt = AIRPORTS[dIcao];
  // The server's positions first (portal airport table), the bundled list as
  // a fallback: on the list alone, MMMY → CYYZ had no departure marker and a
  // progress bar stuck at 0 %.
  const dep = airportLatLon(oIcao, f.orig_pos);
  const arr = airportLatLon(dIcao, f.dest_pos);
  const cur = (lat && lon) ? [lat, lon] : null;
  const total = dep && arr ? haversineNm(dep, arr) : 0;
  const flown = dep && cur ? haversineNm(dep, cur) : 0;
  const progress = total > 0 ? Math.max(0, Math.min(1, flown / total)) : 0;
  const type = String(f.type || "").toUpperCase();
  return {
    ...f,
    lat, lon, oIcao, dIcao,
    depCity: oApt?.city || oIcao,
    arrCity: dApt?.city || dIcao,
    dep, arr, cur,
    total: Math.round(total),
    flown: Math.round(flown),
    progress,
    cat: AC_CAT[type] || "narrow",
    hdgDeg: normHdg(f.hdg),
    // The SimBrief navlog, snapshotted server-side (migration 008). Null when
    // the pilot never pinned an OFP to the bid — then the great-circle below
    // is all there is, and it is labelled as the estimate it is.
    route: Array.isArray(f.route) && f.route.length > 1 ? f.route : null,
    // null is "we could not read the VATSIM datafeed", NOT "offline".
    vatsimOnline: f.vatsim_online === undefined ? null : f.vatsim_online,
    vatsimCallsign: f.vatsim_callsign || null,
    vatsimCid: f.vatsim_cid || null,
    hasPosition: f.has_position ?? !!(lat && lon),
    callsign: `AC${f.flt || ""}`,
    display: f.aircraft_display || type,
    pilotName: f.pilot_name || "",
    // "VAC003 · Test Pilot", or whichever half is known.
    pilotLabel: [f.pid, f.pilot_name].filter(Boolean).join(" · "),
  };
}

/** VATSIM presence for one flight.
 *
 * Three states, not two. `null` means the datafeed could not be read, and
 * showing OFFLINE then would be a claim about the pilot based on a fact about
 * our own network.
 */
/** The VATSIM datafeed's own state, said once for the whole board.
 *
 * Without this, an outage reads as every tile showing "—" and the only
 * explanation is a tooltip you have to go looking for. One line at the bottom
 * of the map is where a pilot already looks to see whether the map is working.
 */
function VatsimHealthPill({ health }) {
  if (!health) return null;
  const { state, age_sec: age, pilots_online: online } = health;
  // `idle` means nothing has asked the feed yet — a cold cache, not an
  // outage. It used to be reported as "unavailable", which put a permanent
  // datafeed-down message on a board that simply had nobody flying.
  const look = {
    live:        { dot: "hsl(var(--success))", text: `VATSIM · ${online ?? "?"} online` },
    stale:       { dot: "hsl(var(--warning))", text: `VATSIM · last seen ${fmtAge(age)} ago` },
    idle:        { dot: "hsl(var(--muted-foreground) / 0.7)", text: "VATSIM · checking…" },
    unavailable: { dot: "hsl(var(--muted-foreground) / 0.7)", text: "VATSIM · datafeed unavailable" },
  }[state] || { dot: "hsl(var(--muted-foreground) / 0.7)", text: `VATSIM · ${state}` };

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-sm border font-mono text-[11px]"
      style={{ background: "hsl(var(--card))", borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
      data-testid="mvl-pill-vatsim"
      data-state={state}
      title={
        state === "live" ? "Presence badges are current"
        : state === "idle" ? "Reading the VATSIM datafeed for the first time"
        : "Presence badges show — because the datafeed could not be read, not because pilots are offline"}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: look.dot }} />
      {look.text}
    </div>
  );
}

function fmtAge(sec) {
  if (sec == null) return "?";
  if (sec < 90) return `${sec}s`;
  return `${Math.round(sec / 60)}m`;
}

/**
 * A dot before an ICAO when someone is working that field on VATSIM.
 *
 * Deliberately a dot and not a label. This line already carries two ICAOs and
 * the aircraft type inside a 300px tile; spelling out "TWR 118.5" here would
 * cost the route its legibility for information the pilot cannot act on from
 * the map. The tooltip carries the detail, and the Flight Plan page carries
 * the ATIS text.
 *
 * Nothing renders when the field is unstaffed OR when the feed is unreadable.
 * Absence therefore means "no reason to show a dot", not "uncontrolled" — the
 * board is a glance surface, and the one place that must state the difference
 * out loud is the briefing, which does.
 */
function AtcDot({ icao, atc }) {
  const e = icao && atc?.airports?.[icao];
  if (!e) return null;
  const label = [(e.facilities || []).join(" "), e.atis ? `ATIS ${e.atis}` : null]
    .filter(Boolean).join(" · ");
  return (
    <span
      title={`${icao}: ${label || "staffed"}`}
      style={{
        display: "inline-block", width: 5, height: 5, borderRadius: "50%",
        background: "hsl(var(--success))", marginRight: 4, verticalAlign: "middle",
      }}
      data-testid={`mvl-atc-${icao}`}
    />
  );
}

function VatsimBadge({ online, callsign, cid }) {
  if (online === null || online === undefined) {
    // Two different unknowns, and they were collapsing into one dash. A pilot
    // with no CID on file is a fact about THEM that will never resolve; a
    // readable feed we could not reach is a fact about US that will.
    const noId = !cid;
    return (
      <span
        title={noId
          ? "No VATSIM ID on this pilot's profile"
          : "VATSIM datafeed unavailable — presence unknown"}
        data-testid={noId ? "mvl-vatsim-noid" : "mvl-vatsim-unknown"}
        className="px-1.5 py-0.5 text-[9px] font-bold tracking-widest rounded-sm border"
        style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground) / 0.7)" }}
      >
        {noId ? "NO ID" : "—"}
      </span>
    );
  }
  return online ? (
    <span
      title={callsign ? `Connected to VATSIM as ${callsign}` : "Connected to VATSIM"}
      data-testid="mvl-vatsim-online"
      className="px-1.5 py-0.5 text-[9px] font-bold tracking-widest rounded-sm border"
      style={{ borderColor: "hsl(var(--success) / 0.5)", background: "hsl(var(--success) / 0.12)", color: "hsl(var(--success))" }}
    >
      {callsign || "VATSIM"}
    </span>
  ) : (
    <span
      title="Not connected to VATSIM"
      data-testid="mvl-vatsim-offline"
      className="px-1.5 py-0.5 text-[9px] font-bold tracking-widest rounded-sm border"
      style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }}
    >
      OFFLINE
    </span>
  );
}

// The flight's phase, by Live Tracking's rules (boardPhase in lib/format.js).
const statusFrom = boardPhase;

export default function LiveMap() {
  const { resolvedTheme } = useTheme();
  const [selectedId, setSelectedId] = useState(null);
  const [follow, setFollow] = useState(false);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const listRef = useRef(null);

  // `error` is destructured and USED below. Without it a failing feed rendered
  // as an empty board — "0 active" is what a working system with nobody flying
  // looks like, so a 500 from /live/flights was indistinguishable from a quiet
  // evening. That is how an unapplied migration reads as "the map is broken"
  // with nothing on screen to say so.
  const { data: raw = [], error, isLoading } = useQuery({
    queryKey: ["live-flights"],
    queryFn: live.flights,
    refetchInterval: 15000,
  });

  // The VATSIM datafeed's own health. Polled on ITS clock (it refreshes once a
  // minute), not the board's 15 s, and the endpoint only reads a cache so this
  // cannot become a second source of load on VATSIM.
  const { data: vatsimHealth } = useQuery({
    queryKey: ["vatsim-health"],
    queryFn: live.vatsim,
    refetchInterval: 60000,
    retry: false,
  });

  // Which airports are staffed, for the dots on the tiles. One compact call
  // covering every staffed field — 2.3 KB for 59 airports — rather than a
  // lookup per airport on a board that can show twenty flights. Same 60 s as
  // the health pill, because it reads the same cached snapshot.
  const { data: atcSummary } = useQuery({
    queryKey: ["vatsim-atc-summary"],
    queryFn: () => live.atc(),
    refetchInterval: 60000,
    retry: false,
  });

  const flights = useMemo(() => raw.map(normalize), [raw]);
  const selected = flights.find((f) => f.id === selectedId) || null;

  const filtered = useMemo(() => {
    const qq = q.toLowerCase();
    return flights.filter((f) => {
      if (statusF !== "all" && statusFrom(f) !== statusF) return false;
      if (typeF !== "all" && f.type !== typeF) return false;
      if (qq) {
        return (
          f.callsign.toLowerCase().includes(qq) ||
          String(f.flt || "").toLowerCase().includes(qq) ||
          f.oIcao.toLowerCase().includes(qq) ||
          f.dIcao.toLowerCase().includes(qq) ||
          f.pilotName.toLowerCase().includes(qq) ||
          String(f.pid || "").toLowerCase().includes(qq)
        );
      }
      return true;
    });
  }, [flights, q, statusF, typeF]);

  const stats = useMemo(() => {
    const pilots = new Set(flights.map((f) => f.pid)).size;
    return { active: flights.length, pilots };
  }, [flights]);

  const types = useMemo(() => {
    return ["all", ...Array.from(new Set(flights.map((f) => f.type).filter(Boolean))).sort()];
  }, [flights]);

  // Airports referenced by any active flight (unique).
  const referencedApts = useMemo(() => {
    const seen = {};
    flights.forEach((f) => {
      if (f.dep && !seen[f.oIcao]) seen[f.oIcao] = f.dep;
      if (f.arr && !seen[f.dIcao]) seen[f.dIcao] = f.arr;
    });
    return seen;
  }, [flights]);

  return (
    <div className="h-full flex" data-testid="livemap-page" style={{ background: "hsl(var(--background))" }}>
      {/* ================= SIDEBAR ================= */}
      <aside
        className="flex flex-col shrink-0"
        style={{ width: 340, background: "hsl(var(--card))", borderRight: "1px solid hsl(var(--border))" }}
      >
        <div className="px-4 py-3 flex items-center gap-3 border-b" style={{ borderColor: "hsl(var(--border))" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>
              Live Flight Tracker
            </div>
            <div className="text-lg font-extrabold tracking-tight">
              VACARS<span style={{ color: RED }}>5</span> Live
            </div>
          </div>
          <span
            className="ml-auto w-2 h-2 rounded-full"
            style={{ background: RED, animation: "pulse 2s ease-in-out infinite" }}
            title="Live"
          />
        </div>

        {/* The feed failed. Say so — an empty board is what "nobody is flying"
            looks like, and the two must not be the same picture. */}
        {error && (
          <div
            className="px-4 py-3 text-xs border-b"
            style={{ background: "hsl(var(--primary) / 0.12)", borderColor: "hsl(var(--border))", color: "hsl(var(--primary))" }}
            data-testid="mvl-feed-error"
          >
            <div className="font-bold mb-1">Live feed unavailable</div>
            <div className="font-mono text-[11px] break-words">
              {error.response?.status ? `HTTP ${error.response.status} · ` : ""}
              {error.response?.data?.detail || error.message || "Request failed"}
            </div>
            <div className="mt-1" style={{ color: "hsl(var(--muted-foreground))" }}>
              The board below is empty because the request failed, not because
              nobody is flying.
            </div>
          </div>
        )}
        {!error && !isLoading && flights.length === 0 && (
          <div className="px-4 py-3 text-xs border-b" style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }}
               data-testid="mvl-feed-empty">
            No flights active right now.
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-px" style={{ background: "hsl(var(--border))" }}>
          <div className="px-4 py-3" style={{ background: "hsl(var(--card))" }}>
            <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Active</div>
            <div className="text-xl font-extrabold" data-testid="mvl-stat-active">{stats.active}</div>
          </div>
          <div className="px-4 py-3" style={{ background: "hsl(var(--card))" }}>
            <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Pilots</div>
            <div className="text-xl font-extrabold" data-testid="mvl-stat-pilots">{stats.pilots}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-3 space-y-2 border-b" style={{ borderColor: "hsl(var(--border))" }}>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "hsl(var(--muted-foreground))" }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search flight, ICAO, pilot…"
              data-testid="mvl-search"
              className="w-full pl-8 pr-2 py-2 text-xs font-mono rounded-sm border"
              style={{ background: "hsl(var(--secondary))", borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={statusF}
              onChange={(e) => setStatusF(e.target.value)}
              data-testid="mvl-filter-status"
              className="w-full px-2 py-1.5 text-[11px] rounded-sm border"
              style={{ background: "hsl(var(--secondary))", borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            >
              <option value="all">All phases</option>
              {BOARD_PHASES.map((p) => (
                <option key={p} value={p}>{p.charAt(0) + p.slice(1).toLowerCase()}</option>
              ))}
            </select>
            <select
              value={typeF}
              onChange={(e) => setTypeF(e.target.value)}
              data-testid="mvl-filter-type"
              className="w-full px-2 py-1.5 text-[11px] rounded-sm border"
              style={{ background: "hsl(var(--secondary))", borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            >
              {types.map((t) => (
                <option key={t} value={t}>{t === "all" ? "All types" : t}</option>
              ))}
            </select>
          </div>
        </div>

        {/* List head */}
        <div
          className="px-4 py-2 flex items-center justify-between border-b"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>
            Live Flights
          </div>
          <div className="text-[11px] font-mono" style={{ color: "hsl(var(--muted-foreground))" }}>
            {filtered.length} / {flights.length}
          </div>
        </div>

        {/* List */}
        <div ref={listRef} className="flex-1 overflow-y-auto" data-testid="mvl-list">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
              No flights match your filters.
            </div>
          ) : (
            filtered.map((f) => {
              const s = statusFrom(f);
              const isSel = f.id === selectedId;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setSelectedId(f.id)}
                  data-testid={`mvl-flight-${f.callsign}`}
                  className="w-full text-left px-4 py-3 block border-b transition-colors"
                  style={{
                    background: isSel ? "hsl(var(--secondary))" : "transparent",
                    borderLeftWidth: 4,
                    borderLeftStyle: "solid",
                    borderLeftColor: isSel ? RED : "transparent",
                    paddingLeft: isSel ? 12 : 16,
                    borderBottomColor: "hsl(var(--border))",
                    color: "hsl(var(--foreground))",
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 font-mono text-sm font-semibold">
                      {f.hdgDeg === null ? (
                        <span style={{ display: "inline-flex", width: 12, height: 12, alignItems: "center", justifyContent: "center" }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", border: `2px solid ${RED}`, boxSizing: "border-box" }} />
                        </span>
                      ) : (
                        <span style={{ color: RED, transform: `rotate(${f.hdgDeg}deg)`, display: "inline-flex", width: 12, height: 12 }}>
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                            <path d="M12 2 L13 9 L22 12 L22 14 L13 13 L13 19 L15 20 L15 21 L9 21 L9 20 L11 19 L11 13 L2 14 L2 12 L11 9 Z"/>
                          </svg>
                        </span>
                      )}
                      <span>{f.callsign}</span>
                      <VatsimBadge online={f.vatsimOnline} callsign={f.vatsimCallsign} cid={f.vatsimCid} />
                    </div>
                    <span className={`phase-pill ${phaseTone(s)}`} data-testid={`mvl-phase-${f.callsign}`}>
                      {s}
                    </span>
                  </div>
                  <div className="mt-1 flex justify-between text-[11px] font-mono" style={{ color: "hsl(var(--muted-foreground))" }}>
                    <span>
                      <AtcDot icao={f.oIcao} atc={atcSummary} />{f.oIcao}
                      {" → "}
                      <AtcDot icao={f.dIcao} atc={atcSummary} />{f.dIcao}
                    </span>
                    <span className="uppercase">{f.type}</span>
                  </div>
                  {f.pilotLabel && (
                    <div className="mt-0.5 text-[11px] truncate" style={{ color: "hsl(var(--foreground))" }}
                         data-testid={`mvl-pilot-${f.id}`}>
                      {f.pilotLabel}
                    </div>
                  )}
                  <div className="mt-2 h-1 rounded-sm overflow-hidden" style={{ background: "hsl(var(--secondary))" }}>
                    <div style={{ background: RED, height: "100%", width: `${f.progress * 100}%` }} />
                  </div>
                  <div className="mt-1 flex justify-between text-[10px] font-mono" style={{ color: "hsl(var(--muted-foreground))" }}>
                    <span>{f.alt ? `FL${Math.round(Number(f.alt) / 100)}` : "GND"}</span>
                    <span>{f.spd || 0} kt</span>
                    <span>{Math.round(f.progress * 100)}%</span>
                  </div>
                  {/* This flight has INITed but sent no position yet, so it is
                      listed with no symbol on the map. Saying why beats a pilot
                      concluding the tracker is broken. */}
                  {!f.hasPosition && (
                    <div className="mt-1 text-[10px] font-mono" style={{ color: "hsl(var(--muted-foreground) / 0.7)" }}
                         data-testid="mvl-awaiting-telemetry">
                      Awaiting first telemetry — not yet on the map
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* ================= MAP ================= */}
      <div className="flex-1 relative min-w-0">
        <SafeMap
          center={[45, -80]}
          zoom={3}
          style={{ height: "100%", width: "100%", background: "hsl(var(--background))" }}
          worldCopyJump
          zoomControl
        >
          <TileLayer
            attribution={MAP_ATTRIBUTION}
            key={resolvedTheme}
            url={tileUrlFor(resolvedTheme)}
            subdomains={MAP_SUBDOMAINS}
          />
          <FollowSelected selected={selected} follow={follow} />

          {/* Planned route where we have one, great-circle estimate where we
              don't. The two are drawn identically except that the estimate is
              dotted — a straight line between two airports is not a route, and
              should not look like one. */}
          {flights.map((f) => {
            const sel = f.id === selectedId;
            let flownArc, aheadArc, estimated;
            if (f.route) {
              const split = splitRouteAt(f.route, f.cur);
              flownArc = split.flown;
              aheadArc = split.ahead;
              estimated = false;
            } else {
              if (!f.dep || !f.arr) return null;
              flownArc = f.cur ? greatCircleArc(f.dep, f.cur) : [];
              aheadArc = f.cur ? greatCircleArc(f.cur, f.arr) : greatCircleArc(f.dep, f.arr);
              estimated = true;
            }
            return [
              flownArc.length > 1 && (
                <Polyline
                  key={`flown-${f.id}`}
                  positions={flownArc}
                  pathOptions={{
                    color: RED,
                    weight: sel ? 2.2 : 1.5,
                    opacity: sel ? 0.85 : 0.5,
                    dashArray: estimated ? "1 4" : "4 5",
                  }}
                />
              ),
              aheadArc.length > 1 && (
                <Polyline
                  key={`ahead-${f.id}`}
                  positions={aheadArc}
                  pathOptions={{
                    color: sel ? RED : GREY,
                    weight: sel ? 1.5 : 1,
                    opacity: sel ? 0.6 : 0.3,
                    dashArray: estimated ? "1 4" : "4 5",
                  }}
                />
              ),
            ].filter(Boolean);
          })}

          {/* Airport markers */}
          {Object.entries(referencedApts).map(([code, [lat, lon]]) => [
            <CircleMarker
              key={`apt-dot-${code}`}
              center={[lat, lon]}
              radius={3}
              className="map-apt-dot"
              pathOptions={{ fillOpacity: 1, weight: 1 }}
            />,
            <Marker
              key={`apt-lbl-${code}`}
              position={[lat, lon]}
              icon={aptIcon(code)}
              interactive={false}
            />,
          ])}

          {/* Plane markers */}
          {flights.map((f) => {
            if (!f.cur) return null;
            const sel = f.id === selectedId;
            return (
              <Marker
                key={f.id}
                position={f.cur}
                icon={planeIcon(f.hdgDeg, f.cat, sel)}
                zIndexOffset={sel ? 1200 : 1000}
                eventHandlers={{ click: () => setSelectedId(f.id) }}
              >
                <Tooltip direction="top" offset={[0, -12]} opacity={0.95}>
                  <span className="font-mono font-semibold">{f.callsign}</span>
                  {f.pilotLabel && <><br />{f.pilotLabel}</>}
                </Tooltip>
              </Marker>
            );
          })}
        </SafeMap>

        {/* Feed status pills. The dot used to be hardcoded red — it said "live"
            with the same confidence whether the last poll succeeded or 500'd. */}
        <div className="absolute bottom-4 left-4 flex flex-col gap-1.5" style={{ zIndex: 600 }}>
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-sm border font-mono text-[11px]"
            style={{ background: "hsl(var(--card))", borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            data-testid="mvl-pill-feed"
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: error ? "hsl(var(--muted-foreground) / 0.7)" : RED }}
            />
            {error
              ? "VACARS5 live feed · unavailable"
              : `VACARS5 live feed · ${filtered.length} aircraft`}
          </div>
          <VatsimHealthPill health={vatsimHealth} />
        </div>

        {/* Detail panel */}
        {selected && (
          <div
            className="absolute top-4 left-4 rounded-sm overflow-hidden shadow-2xl"
            style={{
              background: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              width: 340,
              zIndex: 1200,
            }}
            data-testid="mvl-detail-panel"
          >
            <div
              className="px-4 py-3 flex items-start justify-between border-b"
              style={{ background: "hsl(var(--secondary))", borderColor: "hsl(var(--border))" }}
            >
              <div>
                <div className="text-xl font-extrabold leading-tight" data-testid="mvl-detail-callsign">
                  {selected.callsign}
                </div>
                <div className="text-[12px] font-semibold font-mono" style={{ color: "hsl(var(--muted-foreground))" }} data-testid="mvl-detail-type">
                  {selected.type}
                </div>
                {selected.pilotLabel && (
                  <div className="text-[12px] mt-0.5" data-testid="mvl-detail-pilot">
                    {selected.pilotLabel}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setSelectedId(null); setFollow(false); }}
                data-testid="mvl-detail-close"
                className="p-1 rounded-sm hover:bg-secondary"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Progress */}
              <div className="relative h-6 rounded-sm border overflow-hidden" style={{ borderColor: "hsl(var(--border))", background: "hsl(var(--secondary))" }}>
                <div
                  className="h-full"
                  style={{
                    width: `${selected.progress * 100}%`,
                    backgroundImage: `repeating-linear-gradient(-45deg, ${RED} 0 6px, ${RED}8c 6px 12px)`,
                  }}
                />
                <div
                  className="absolute inset-0 grid place-items-center text-[11px] font-mono font-bold"
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  {Math.round(selected.progress * 100)}%
                </div>
              </div>
              <div className="text-[11px] font-mono" style={{ color: "hsl(var(--muted-foreground))" }}>
                Flown <span style={{ color: "hsl(var(--foreground))" }}>{selected.flown}nm</span> of{" "}
                <span style={{ color: "hsl(var(--foreground))" }}>{selected.total}nm</span>
              </div>

              {/* Follow */}
              <button
                type="button"
                onClick={() => setFollow((v) => !v)}
                data-testid="mvl-follow"
                className="w-full flex items-center justify-center gap-2 py-2 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors"
                style={{
                  borderColor: follow ? RED : "hsl(var(--border))",
                  background: follow ? RED : "transparent",
                  color: follow ? "#fff" : "hsl(var(--foreground))",
                }}
              >
                {follow ? <CheckCircle2 className="w-3 h-3" /> : <Crosshair className="w-3 h-3" />}
                {follow ? "Following" : "Follow flight"}
              </button>

              {/* Route */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Departure</div>
                  <div className="text-sm font-bold">{selected.depCity} <span className="font-mono text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>({selected.oIcao})</span></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Destination</div>
                  <div className="text-sm font-bold">{selected.arrCity} <span className="font-mono text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>({selected.dIcao})</span></div>
                </div>
              </div>

              {/* Telemetry row 1 */}
              <div className="grid grid-cols-3 gap-3 pt-2 border-t" style={{ borderColor: "hsl(var(--border))" }}>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Heading</div>
                  <div className="font-mono text-sm font-semibold">{selected.hdgDeg === null ? "—" : `${Math.round(selected.hdgDeg)}°`}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Ground Speed</div>
                  <div className="font-mono text-sm font-semibold">{selected.spd || 0}kt</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Altitude</div>
                  <div className="font-mono text-sm font-semibold">{(Number(selected.alt) || 0).toLocaleString()}ft</div>
                </div>
              </div>

              {/* Telemetry row 2 */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Latitude</div>
                  <div className="font-mono text-sm">{Number(selected.pos_lat).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Longitude</div>
                  <div className="font-mono text-sm">{Number(selected.pos_long).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "hsl(var(--muted-foreground))" }}>Fuel</div>
                  <div className="font-mono text-sm">{Number(selected.fuel) || 0} kg</div>
                </div>
              </div>

              {/* Last report */}
              <div className="pt-2 border-t text-[11px] font-mono" style={{ color: "hsl(var(--muted-foreground))", borderColor: "hsl(var(--border))" }}>
                Last VACARS report: {selected.last_upd ? new Date(selected.last_upd * 1000).toUTCString().split(" ").slice(4, 5)[0] + "z" : "—"}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }
        .mvl-plane-icon { background: none !important; border: none !important; }
        .mvl-apt-label  { background: none !important; border: none !important; }
      `}</style>
    </div>
  );
}
